/**
 * ReportBlocksRenderer
 *
 * Shared component that renders a flat list of ReportBlocks.
 * Handles the block formats present in insight reports:
 * - InsightCardBlock: layout-based cards with block_elements (rendered via InsightCardBlockRenderer)
 * - PlanCardBlock: agent analysis plan steps (rendered via AgentThoughtsCard)
 * - FreeFlowBlock: plain UIBlock rendered directly via dispatchBlock
 *
 * Used by InsightCardPayloadRenderer, InsightCardMultiPageRenderer,
 * and BlocksPreviewSlot — single source of truth for report block rendering.
 * Callers are responsible for any outer layout wrapper (e.g. <Container>).
 */

import React from 'react';
import type { ReportBlock, UIActionEvent } from '@/types/ui-blocks';
import { AgentThoughtsCard } from '@strivelabs/ui-blocks';
import { InsightCardBlockRenderer } from './insight-card-block-renderer';
import { ChatCTABlockRenderer } from './chat-cta-block-renderer';
import { dispatchBlock } from './block-renderer-utils';
import {
  isChatCtaBlock,
  isInsightCardBlock,
  isPlanCardBlock,
  mapPlanCardStepsToAgentSteps,
} from './slots/shared';

export interface ReportBlocksRendererProps {
  /** The list of report blocks to render */
  blocks: ReportBlock[];
  /** Callback fired when user interacts with UI components */
  onUIAction?: (event: UIActionEvent) => void;
  /** When true, interactive elements are disabled */
  readOnly?: boolean;
}

export const ReportBlocksRenderer: React.FC<ReportBlocksRendererProps> = ({
  blocks,
  onUIAction = () => {},
  readOnly,
}) => {
  return (
    <div className="flex flex-col gap-4">
      {blocks.map((block, index) => {
        if (isInsightCardBlock(block)) {
          return (
            <InsightCardBlockRenderer
              key={block.id}
              block={block}
              onUIAction={onUIAction}
            />
          );
        }

        if (isPlanCardBlock(block)) {
          return (
            <AgentThoughtsCard
              key={block.id ?? `plan-card-${index}`}
              steps={mapPlanCardStepsToAgentSteps(
                block.content.steps
              )}
              label={block.content.label}
              detailButtonLabel={
                block.content.detail_button_label
              }
              dialogTitle={block.content.dialog_title}
              dialogDescription={
                block.content.dialog_description
              }
            />
          );
        }

        if (isChatCtaBlock(block)) {
          return <ChatCTABlockRenderer key={block.id} element={block} />;
        }

        return (
          <div key={block.id || `free-flow-${index}`}>
            {dispatchBlock({
              block,
              onUIAction,
              readOnly,
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ReportBlocksRenderer;
